import { initAnalyticsLazy } from "@/analytics/firebaseAnalytics";
import type {
  AnalyticsContext,
  AnalyticsEventKey,
  AnalyticsEventParams,
  AnalyticsSettings,
  AnalyticsSettingsUpdate,
  WebVitalMetric,
} from "./types";
import { isConsentGranted } from "./consent";

/**
 * Core analytics implementation backed by Firebase Analytics with graceful
 * fallbacks to `gtag` when the Firebase instance is unavailable.
 *
 * The module exposes high-level tracking helpers and manages initialization,
 * context propagation, and feature toggles for individual event types.
 */

/** Singleton Firebase analytics instance used for event dispatch. */
let analyticsInstance: any | null = null;
/** Flag used to ensure we only attempt initialization once at a time. */
let initializing = false;
/** Queue of operations to execute once the analytics instance becomes available. */
const pendingQueue: Array<() => void> = [];
/** Upper bound to prevent unbounded queue growth on unsupported environments. */
const MAX_PENDING_QUEUE = 500;

/** Default settings applied when no overrides have been provided. */
const defaultSettings: AnalyticsSettings = {
  enabled: true,
  implementation: "pro",
  respectConsent: true,
  events: {
    page_view: true,
    view_section: true,
    click_cta: true,
    click_element: true,
    generate_lead: true,
    scroll: true,
    web_vital: true,
  },
};

/** Mutable snapshot of the active analytics configuration. */
let currentSettings: AnalyticsSettings = { ...defaultSettings };

/**
 * Lazily creates the Firebase analytics instance when required.
 *
 * The function enforces single-flight initialization, captures errors silently,
 * and replays any operations queued while initialization was in progress.
 */
async function ensureAnalytics() {
  if (!currentSettings.enabled) return null;
  if (analyticsInstance) return analyticsInstance;
  if (initializing) return null;
  initializing = true;
  try {
    analyticsInstance = await initAnalyticsLazy();
  } catch {
    analyticsInstance = null;
  } finally {
    initializing = false;
    // Always flush queued tasks so gtag fallback can run when Firebase is unavailable.
    while (pendingQueue.length) {
      const fn = pendingQueue.shift();
      try {
        if (fn) {
          fn();
        }
      } catch {
        // ignore
      }
    }
  }
  return analyticsInstance;
}

/** Latest contextual metadata to attach to outgoing analytics events. */
let currentContext: AnalyticsContext = {};

/**
 * Merges the supplied context values into the active analytics context.
 *
 * @param ctx - Partial context values describing the current page or session.
 */
export function setContext(ctx: AnalyticsContext) {
  currentContext = { ...currentContext, ...ctx };
}

function isEventEnabled(name: string): boolean {
  const key = name as AnalyticsEventKey;
  if (Object.prototype.hasOwnProperty.call(currentSettings.events, key)) {
    return currentSettings.events[key as AnalyticsEventKey];
  }
  return true;
}

/**
 * Determines whether an event should be tracked based on configuration and consent.
 *
 * @param name - Name of the event being evaluated.
 */
function shouldTrack(name: string): boolean {
  if (!currentSettings.enabled) return false;
  if (!isEventEnabled(name)) return false;
  if (currentSettings.respectConsent && !isConsentGranted()) return false;
  return true;
}

/**
 * Augments event parameters with contextual metadata such as site identifiers and
 * the current location URL.
 */
function withContext(params?: AnalyticsEventParams): AnalyticsEventParams {
  return {
    ...params,
    site_id: currentContext.site_id,
    domain_type: currentContext.domain_type,
    domain: currentContext.domain,
    language: currentContext.language,
    version_id: currentContext.version_id,
    page_location:
      typeof window !== "undefined" ? window.location.href : undefined,
  };
}

/** Adds an operation to the queue to run once analytics is available. */
function enqueue(fn: () => void) {
  if (pendingQueue.length >= MAX_PENDING_QUEUE) {
    pendingQueue.shift();
  }
  pendingQueue.push(fn);
}

/**
 * Tracks an arbitrary analytics event.
 *
 * Dispatch prioritizes Firebase Analytics when available, falling back to
 * a global `gtag` instance. When analytics is still initializing, the event is
 * enqueued and replayed once initialization completes.
 *
 * @param name - Event name compatible with GA4.
 * @param params - Additional payload parameters.
 */
export function trackEvent(name: string, params?: AnalyticsEventParams) {
  if (typeof window === "undefined") return;
  if (!shouldTrack(name)) return;
  const payload = withContext(params);

  const exec = () => {
    try {
      if (analyticsInstance) {
        import("firebase/analytics")
          .then((mod: any) => {
            const logEvent = mod.logEvent;
            const getAnalytics = mod.getAnalytics;
            const isSupported = mod.isSupported;
            if (isSupported) {
              isSupported().then((ok: boolean) => {
                if (!ok) return;
                logEvent(getAnalytics(), name, payload);
              });
            } else {
              logEvent(getAnalytics(), name, payload);
            }
          })
          .catch(() => {
            /* ignore */
          });
      } else if ((window as any).gtag) {
        (window as any).gtag("event", name, payload);
      }
    } catch {
      // ignore
    }
  };

  if (!analyticsInstance) {
    ensureAnalytics();
    enqueue(exec);
    return;
  }
  exec();
}

/** Records a page view event. */
export function trackPageView(params?: AnalyticsEventParams) {
  trackEvent("page_view", params);
}

/** Records a section view event for in-page navigation or visibility tracking. */
export function trackSectionView(section_key: string, section_index: number) {
  trackEvent("view_section", { section_key, section_index });
}

/** Payload describing a CTA interaction. */
export type TrackCTAParams = {
  cta_id: string;
  cta_text?: string;
  destination_type?: string;
  destination_value?: string;
  section_key?: string;
  section_index?: number;
  element_type?: string;
  [key: string]: string | number | boolean | undefined;
};

/** Tracks clicks on prominent calls-to-action. */
export function trackCTA(params: TrackCTAParams) {
  trackEvent("click_cta", params as AnalyticsEventParams);
}

/** Records the outcome of a lead generation flow such as a form submission. */
export function trackLead(params: { form_id?: string; success?: boolean }) {
  trackEvent("generate_lead", params as any);
}

/** Tracks scroll depth measurements. */
export function trackScroll(percent: number) {
  trackEvent("scroll", { percent });
}

/** Records Core Web Vital metrics to GA4. */
export function trackWebVital(metric: WebVitalMetric) {
  trackEvent("web_vital", {
    metric_name: metric.name,
    metric_value: Math.round(metric.value),
  });
}

/** Ensures the analytics SDK is initialized when analytics is enabled. */
export async function initAnalytics() {
  if (!currentSettings.enabled) return;
  await ensureAnalytics();
}

/** Returns a copy of the active analytics context. */
export function getContext(): AnalyticsContext {
  return { ...currentContext };
}

/**
 * Updates the current analytics configuration, merging nested event toggles.
 *
 * @param update - Partial configuration or full settings object.
 */
export function setSettings(
  update: AnalyticsSettingsUpdate | AnalyticsSettings,
) {
  const eventsUpdate =
    "events" in update && update.events ? update.events : undefined;

  currentSettings = {
    ...currentSettings,
    ...update,
    events: eventsUpdate
      ? {
          ...currentSettings.events,
          ...eventsUpdate,
        }
      : "events" in update && update.events
        ? (update.events as AnalyticsSettings["events"])
        : currentSettings.events,
  } as AnalyticsSettings;

  if (!currentSettings.enabled) {
    analyticsInstance = null;
  }
}

/** Returns a snapshot of the current analytics configuration. */
export function getSettings(): AnalyticsSettings {
  return { ...currentSettings, events: { ...currentSettings.events } };
}
